import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useInventarioLan } from "@/hooks/useConfig";
import type { InventarioLanAgencia } from "@/api/types";

const CLASE: Record<
  string,
  { texto: string; variant: "secondary" | "destructive" | "outline" }
> = {
  gestionado: { texto: "Gestionado", variant: "outline" },
  // Conectado a la red del cliente y nadie lo vigila. Es el hallazgo.
  sin_gestionar: { texto: "Sin vigilar", variant: "destructive" },
  // HikCentral lo da de alta pero el router no lo ve.
  fantasma: { texto: "Fantasma", variant: "secondary" },
};

function Tarjeta({ titulo, valor, nota }: { titulo: string; valor: number; nota?: string }) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-xs text-muted-foreground">{titulo}</div>
      <div className="text-2xl font-semibold">{valor}</div>
      {nota && <div className="text-xs text-muted-foreground">{nota}</div>}
    </div>
  );
}

function Detalle({ a }: { a: InventarioLanAgencia }) {
  // Primero lo que nadie vigila: es lo accionable.
  const orden = { sin_gestionar: 0, fantasma: 1, gestionado: 2 } as Record<string, number>;
  const hosts = [...(a.hosts ?? [])].sort(
    (x, y) => (orden[x.clase] ?? 9) - (orden[y.clase] ?? 9),
  );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>IP</TableHead>
          <TableHead>MAC</TableHead>
          <TableHead>Nombre</TableHead>
          <TableHead>Clase</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {hosts.map((h) => {
          const c = CLASE[h.clase] ?? CLASE.gestionado;
          return (
            <TableRow key={`${h.ip}-${h.mac ?? "sin-mac"}`}>
              <TableCell className="font-mono text-xs">{h.ip}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {h.mac ?? "—"}
              </TableCell>
              <TableCell className="text-xs">
                {h.nombre_hikcentral ?? h.nombre_dhcp ?? (
                  <span className="text-muted-foreground">sin nombre</span>
                )}
                {h.modelo && (
                  <span className="ml-1 text-muted-foreground">· {h.modelo}</span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={c.variant}>{c.texto}</Badge>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export function InventarioLanPanel() {
  const { data, isLoading, isError } = useInventarioLan();
  const [abierta, setAbierta] = useState<string | null>(null);

  if (isLoading)
    return <p className="text-sm text-muted-foreground">Cargando inventario…</p>;
  if (isError || !data)
    return <p className="text-sm text-destructive">No se pudo consultar el inventario.</p>;

  const t = data.totales;
  const agencias = data.agencias ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tarjeta titulo="Vivos en la LAN" valor={t?.vivos_en_lan ?? 0} />
        <Tarjeta titulo="Gestionados" valor={t?.gestionados ?? 0} nota="HikCentral" />
        <Tarjeta
          titulo="Sin vigilar"
          valor={t?.sin_gestionar_equipos ?? 0}
          nota="equipos únicos por MAC"
        />
        <Tarjeta titulo="Fantasmas" valor={t?.fantasmas ?? 0} nota="dados de alta, no vistos" />
      </div>

      <p className="text-xs text-muted-foreground">
        {t?.agencias_con_datos ?? 0} agencias con datos ·{" "}
        {t?.agencias_sin_datos ?? 0} sin cosechar. El conteo de equipos sin vigilar es
        por MAC única: una misma máquina puede aparecer en dos IPs si el router
        guarda una entrada vieja. Y la tabla ARP sólo ve lo que habló hace poco, así
        que un equipo encendido pero callado no aparece.
      </p>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agencia</TableHead>
            <TableHead className="text-right">Vivos</TableHead>
            <TableHead className="text-right">Gestionados</TableHead>
            <TableHead className="text-right">Sin vigilar</TableHead>
            <TableHead className="text-right">Fantasmas</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {agencias.map((a) => (
            <TableRow key={a.device_id}>
              <TableCell className="font-medium">
                {a.agencia ?? <span className="text-muted-foreground">{a.ip}</span>}
                {a._status !== "ok" && (
                  <span className="ml-2 text-xs text-amber-600">
                    sin datos — {a.motivo}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right">{a.vivos_en_lan ?? "—"}</TableCell>
              <TableCell className="text-right">{a.gestionados ?? "—"}</TableCell>
              <TableCell
                className={
                  (a.sin_gestionar_equipos ?? 0) > 0
                    ? "text-right font-medium text-destructive"
                    : "text-right"
                }
              >
                {a.sin_gestionar_equipos ?? "—"}
              </TableCell>
              <TableCell className="text-right">{a.fantasmas ?? "—"}</TableCell>
              <TableCell className="text-right">
                {a._status === "ok" && (
                  <button
                    type="button"
                    onClick={() =>
                      setAbierta(abierta === a.device_id ? null : a.device_id)
                    }
                    className="rounded border px-2 py-0.5 text-xs hover:bg-muted"
                  >
                    {abierta === a.device_id ? "Ocultar" : "Ver equipos"}
                  </button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {abierta && (
        <div className="rounded-md border p-2">
          <Detalle a={agencias.find((a) => a.device_id === abierta)!} />
        </div>
      )}
    </div>
  );
}
